import { LoggableContentType } from '@app-types/PageType'
import { type UserDataType } from '@app-types/UserDataType'
import { DailyStatsType } from '@components/Home/Stats'
import dayjs from 'dayjs'

const removeStatsLongerThan70Days = (stats: DailyStatsType) => {
  if (!stats) return {}

  const now = dayjs()
  const statsToRemove = Object.keys(stats).filter((key) => {
    try {
      // Validate key format with regex for DD-MM-YYYY
      if (!/^\d{2}-\d{2}-\d{4}$/.test(key)) return false

      const [day, month, year] = key.split('-')
      const date = dayjs(`${year}-${month}-${day}`)

      // Check if date is valid before performing diff
      if (!date.isValid()) return false

      return now.diff(date, 'day') > 70
    } catch (error) {
      console.error('Error processing date for key:', key, error)
      return false // Don't remove stats with malformed dates
    }
  })

  // Create a new object instead of mutating the original
  const newStats = { ...stats }
  statsToRemove.forEach((key) => {
    delete newStats[key]
  })
  return newStats
}

export const getScoreBasedOnType = (
  type: LoggableContentType | string,
  count: number,
) => {
  switch (type) {
    case 'kuran':
      return count * 2
    case 'cevsen':
      return count * 5
    case 'cevsen-i-serif':
      return count / 4
    case 'celcelutiye':
      return count
    case 'sabah-dualari':
    case 'aksam-dualari':
      return count * 1.5
    case 'teheccudNamazi':
    case 'nafileNamaz':
    case 'kazaNamazi':
      return count * 2
    case 'teravih':
      return count * 20
    case 'oruc':
      return count * 4

    default:
      // Custom stats get a default score of 1 per count
      return count
  }
}

type CalculateStatsArgs = {
  type: LoggableContentType | string
  readPageCount: number
  userData: UserDataType
  date?: StatsDateOption
}

export type StatsDateOption = string

export function calculateAndGetNewStats({
  type,
  readPageCount,
  userData,
  date = dayjs().format('DD-MM-YYYY'),
}: CalculateStatsArgs): DailyStatsType {
  // Defensive: Get the current stats or an empty object if undefined
  const currentStats = userData.stats || {}

  const score = getScoreBasedOnType(type, readPageCount)
  // Get current stats for this day, with a default empty stat object if none exist
  const statsOfDay = currentStats[date] || { score: 0 }

  // Calculate new values
  const currentScoreOfTheDay = (statsOfDay?.score || 0) + score

  // Calculate the new value for this specific stat type
  const valueOfType = (statsOfDay?.[type] || 0) + readPageCount

  // Create updated entry for this date
  const updatedDayStats = {
    ...(currentStats[date] || {}), // Start with existing stats for this day
    score: Number(currentScoreOfTheDay), // Update the score
    [type]: valueOfType, // Update the specific stat type
  }

  // Create new stats object with updated entry
  const newStats = {
    ...currentStats, // Keep all existing dates' stats
    [date]: updatedDayStats, // Update this date's stats
  }

  try {
    return removeStatsLongerThan70Days(newStats)
  } catch (error) {
    console.error('Error removing old stats:', error)
    return newStats // Return stats without removing old ones in case of error
  }
}
